import React from "react";
import { WifiOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";

/**
 * Full-screen fallback shown whenever the device loses internet access.
 *
 * It sits above everything (including toasts) and blocks all interaction with
 * the app underneath. That is intentional: on a dead connection the app was
 * either stuck on a blank/skeleton screen or letting the user click around and
 * spam failed requests + error toasts. This overlay replaces that with a
 * single, calm "you're offline" state and does NOT navigate anywhere, so no
 * further requests (and therefore no further error toasts) can be triggered.
 *
 * The app resumes automatically once the connection returns (the `online`
 * event flips the state back), and the user can force a re-check with the
 * button.
 */
export default function OfflineFallback() {
  const { isOnline, isChecking, recheck } = useNetworkStatus();

  if (isOnline) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-live="assertive"
      aria-label="No internet connection"
      style={{
        // Max z-index so it also covers any toasts that were already queued.
        zIndex: 2147483647,
        // Keep content clear of the notch / status bar / home indicator.
        paddingTop: "var(--safe-area-inset-top)",
        paddingBottom: "var(--safe-area-inset-bottom)",
      }}
      className="fixed inset-0 flex items-center justify-center bg-background px-6"
    >
      <div className="flex w-full max-w-sm flex-col items-center text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <WifiOff className="h-8 w-8" />
        </div>

        <h2 className="mt-6 text-xl font-semibold text-foreground">
          You're offline
        </h2>

        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          We can't reach the internet right now. Please check your connection —
          the app will pick up right where you left off once you're back online.
        </p>

        <Button
          onClick={recheck}
          disabled={isChecking}
          className="mt-6 min-w-[140px] rounded-lg font-medium"
        >
          <RefreshCw className={`h-4 w-4 ${isChecking ? "animate-spin" : ""}`} />
          {isChecking ? "Checking…" : "Try again"}
        </Button>
      </div>
    </div>
  );
}
